/**
 * Search-param coercion + API mapping for the billing list filters (LR-551 / T14).
 *
 * Kept as a standalone module (like `features/trades/trade-detail-search.ts`) so
 * the URL round-trip and the year/month -> period translation are directly
 * unit-testable without importing router.tsx or rendering the page.
 */
import type { BillingStatementListParams } from '@/services/billing/billing-service';
import type { BillingStatementType, BillingStatus } from '@/types';
import { parsePositiveIntParam } from '@/utils/search-params';

/**
 * Document type of a statement — NOT the `agreement` (gmsla/gmra) of the
 * underlying master agreement. Mirae R11.4 names exactly these two: "GMSLA
 * Billing" and "Cash Pool Interest Statement (per currency per CP)".
 *
 * This is the URL/filter vocabulary and is deliberately its own union rather
 * than the wire one (`BillingStatementType` in `@/types`): 'billing' is the
 * short token that has been in shipped links since LR-551, and renaming it
 * would break every bookmarked `/billing?type=billing`. The mapping to the API
 * value lives in `params` below.
 */
export type BillingStatementTypeFilter = 'billing' | 'cash_pool';

export interface BillingStatementTypeOption {
  value: BillingStatementTypeFilter;
  label: string;
  /** Query params that narrow the list to this document type. */
  params: BillingStatementListParams;
}

export const BILLING_STATEMENT_TYPES: readonly BillingStatementTypeOption[] = [
  // 'Trade Billing', not R11.4's 'GMSLA Billing': the option really does send
  // `statement_type=trade_billing`, which is the server's own display string.
  // That type also covers the legacy GMRA statements in the table, which the
  // list endpoint has left out since LR-632 (still reachable by direct link),
  // so today the label and 'GMSLA' name the same rows. The URL token stays
  // `billing`, so shared links keep working.
  { value: 'billing', label: 'Trade Billing', params: { statement_type: 'trade_billing' } },
  {
    value: 'cash_pool',
    label: 'Cash Pool Interest',
    params: { statement_type: 'cash_pool_interest' },
  },
];

/**
 * The statuses the app can reach: mark-paid moves draft -> paid directly
 * (LR-556). 'issued' is still a backend choice, but no app action sets it
 * (product 2026-07-21, LR-681); a row an admin edit put there is listed under
 * All statuses. A stale `?status=issued` link just drops the status filter.
 */
export const BILLING_STATUS_OPTIONS: readonly { value: BillingStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'paid', label: 'Paid' },
];

export const BILLING_MONTHS: readonly { value: number; label: string }[] = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

/** Filter state, mirrored 1:1 into the `/billing` query string. */
export interface BillingSearch {
  year?: number;
  /** Only meaningful together with `year` — a month alone has no period bounds. */
  month?: number;
  /** Counterparty id (exact match server-side, LR-551). */
  cp?: number;
  type?: BillingStatementTypeFilter;
  status?: BillingStatus;
}

/** Years offered in the Year select: the current year down to `span` years back. */
export const billingYearOptions = (currentYear: number, span = 5): number[] =>
  Array.from({ length: span + 1 }, (_, i) => currentYear - i);

// Shared with the trade/collateral routes' `parseCp` (LR-587): the rule is about
// how the ROUTER hands over a search value, not about billing.
const parsePositiveInt = parsePositiveIntParam;

const parseYear = (value: unknown): number | undefined => {
  const n = parsePositiveInt(value);
  // Reject junk that would otherwise build an absurd period range (?year=7).
  return n !== undefined && n >= 1970 && n <= 9999 ? n : undefined;
};

const parseMonth = (value: unknown): number | undefined => {
  const n = parsePositiveInt(value);
  return n !== undefined && n <= 12 ? n : undefined;
};

const parseType = (value: unknown): BillingStatementTypeFilter | undefined =>
  BILLING_STATEMENT_TYPES.find((t) => t.value === value)?.value;

const parseStatus = (value: unknown): BillingStatus | undefined =>
  BILLING_STATUS_OPTIONS.find((s) => s.value === value)?.value;

/**
 * Narrow a raw query object to the billing filter state. Every unrecognised or
 * out-of-range value collapses to `undefined` (= filter not applied) so a
 * hand-edited or stale link renders the unfiltered list instead of crashing or
 * sending garbage to the API.
 *
 * `month` is dropped without a `year`: on its own it cannot be translated into a
 * period range, and the UI disables it for the same reason.
 */
export const parseBillingSearch = (search: Record<string, unknown>): BillingSearch => {
  const year = parseYear(search.year);
  return {
    year,
    month: year === undefined ? undefined : parseMonth(search.month),
    cp: parsePositiveInt(search.cp),
    type: parseType(search.type),
    status: parseStatus(search.status),
  };
};

/** Last calendar day of `year`-`month` (1-based month). */
export const lastDayOfMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

const iso = (year: number, month: number, day: number): string =>
  `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/**
 * Translate the filter state into statements-endpoint query params.
 *
 * The API has no year/month params — it filters on `period_start`, so a calendar
 * selection becomes a closed date range: a year is Jan 1 - Dec 31, a year+month
 * is the 1st - last day of that month. Keys with no selection are omitted
 * entirely (not sent empty), so they stay out of the request and out of the
 * react-query cache key.
 */
export const billingSearchToParams = (search: BillingSearch): BillingStatementListParams => {
  const params: BillingStatementListParams = {};

  if (search.year !== undefined) {
    const month = search.month;
    if (month !== undefined) {
      params.period_start_gte = iso(search.year, month, 1);
      params.period_start_lte = iso(search.year, month, lastDayOfMonth(search.year, month));
    } else {
      params.period_start_gte = iso(search.year, 1, 1);
      params.period_start_lte = iso(search.year, 12, 31);
    }
  }
  if (search.cp !== undefined) params.counterparty = search.cp;
  if (search.status !== undefined) params.status = search.status;
  if (search.type !== undefined) {
    Object.assign(params, BILLING_STATEMENT_TYPES.find((t) => t.value === search.type)?.params);
  }

  return params;
};

/**
 * The wire `statement_type` a filter token stands for (LR-624).
 *
 * Derived from `BILLING_STATEMENT_TYPES` rather than spelled out a second time:
 * the list's filter and the MTD preview's document switch are the SAME choice,
 * and two hand-written mappings of one vocabulary are how the two screens drift
 * apart on the next value.
 */
export const billingStatementTypeOf = (
  type: BillingStatementTypeFilter | undefined
): BillingStatementType | undefined =>
  BILLING_STATEMENT_TYPES.find((t) => t.value === type)?.params.statement_type;

/** True when at least one filter is applied — drives the Reset button + empty state copy. */
export const hasBillingFilters = (search: BillingSearch): boolean =>
  Object.values(search).some((v) => v !== undefined);
